import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api from "../lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Minimum 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset. Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 group">
          <div className="w-9 h-9 rounded-lg bg-[#2A2F2B] flex items-center justify-center transition-soft group-hover:rotate-12">
            <Sparkles className="w-4 h-4 text-[#D18E63]" strokeWidth={2} />
          </div>
          <span className="font-heading text-2xl font-bold text-[#2A2F2B]">uptick</span>
        </Link>
        <div className="rounded-2xl bg-white border border-[#EAE6E1] p-8 animate-fade-up">
          <h1 className="font-heading text-3xl tracking-tight font-medium text-[#2A2F2B] mb-1">
            Set a new password
          </h1>
          <p className="text-sm text-[#6B7068] mb-6">
            Choose a strong password — at least 6 characters.
          </p>
          {!token ? (
            <p className="text-sm text-[#C66B59]">Missing reset token.</p>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7068]">New password</Label>
                <Input
                  type="password"
                  value={password}
                  minLength={6}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5 rounded-lg"
                  data-testid="reset-password-input"
                  required
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7068]">Confirm</Label>
                <Input
                  type="password"
                  value={confirm}
                  minLength={6}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1.5 rounded-lg"
                  data-testid="reset-confirm-input"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-btn"
                className="w-full rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] py-6"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
